import { useEffect } from 'react'

import { ReactiveVar, makeVar, useReactiveVar } from '@apollo/client'

import { isTruthy } from '@local/utils'

const reactiveVarStore: Record<string, ReactiveVar<any>> = {}

const getReactiveVar = <T>(key: string, initialValue?: T) => {
  if (reactiveVarStore[key]) return reactiveVarStore[key] as ReactiveVar<T>

  return (reactiveVarStore[key] = makeVar<T | undefined>(initialValue))
}

const useReactiveSessionStorage = <T>(
  key: string,
  initialValue?: T,
): [T | undefined, (value: T) => void, () => void] => {
  const reactiveVar = getReactiveVar<T | undefined>(key, initialValue)

  const value = useReactiveVar(reactiveVar)

  useEffect(() => {
    if (isTruthy(value)) return

    const storageValue = getStorageValue()

    if (!storageValue) {
      setter(initialValue)
      return
    }

    setter(storageValue)
  }, [key, value, initialValue])

  const setter = (v: T | undefined) => {
    if (typeof v === 'undefined') return
    setStorageValue(v)
    reactiveVar(v)
  }

  const remove = () => {
    setter(undefined)
  }

  const getStorageValue = () => {
    const rawStorageValue = sessionStorage.getItem(key)

    if (!rawStorageValue || rawStorageValue === 'undefined') {
      return undefined
    }

    return JSON.parse(rawStorageValue)
  }

  const setStorageValue = (value: T) => {
    return sessionStorage.setItem(key, JSON.stringify(value))
  }

  return [value, setter, remove]
}

export default useReactiveSessionStorage
